/**
 * 消費税の唯一の入口。
 *
 * これを作った理由:
 *   Checkout.tsx の中だけで既定値が3種類あった。
 *     - 客に請求する税  … shop.taxRate ?? 0     → 未設定なら 0%
 *     - バックの計算    … shop.taxRate || 0.1   → 未設定なら 10%
 *     - 会計に記録する  … shop.taxRate || 0.1   → 未設定なら 10%
 *   つまり「税を取らずに会計したのに、税込10%だった前提でバックを割り戻す」。
 *   キャストのバックが毎回 9.1% 少なくなる。
 *
 *   さらに `||` は 0 を falsy として弾くので、
 *   税率を 0% に設定した店（内税・非課税）が 10% に戻される。
 *
 *   アプリ側は taxRate をそもそも読んでおらず 0.10 決め打ちだった。
 *   同じ内容の伝票が PC とアプリで違う金額になる。
 *
 * 対になる実装: shop_staff_app_v2/lib/utils/tax_rate.dart
 * 両方に同じ検査を当てている（scripts/check-tax-rate.mjs / test/tax_rate_test.dart）。
 */

/** 税率が設定されていない店に使う値。日本の標準税率。 */
export const DEFAULT_TAX_RATE = 0.1;

export interface TaxShopLike {
  taxRate?: number | null;
  priceDisplayType?: string | null;
}

/**
 * 店の税率を返す。
 * 明示的に 0 が入っていれば 0 を返す（`||` と違い 0 を潰さない）。
 * 数値でない・負・1 以上（率でなく％を入れた等）は既定値に倒す。
 */
export function resolveTaxRate(shop: TaxShopLike | null | undefined): number {
  const raw = shop?.taxRate;
  if (typeof raw !== 'number' || !Number.isFinite(raw)) return DEFAULT_TAX_RATE;
  if (raw < 0 || raw >= 1) return DEFAULT_TAX_RATE;
  return raw;
}

/** 店の税率が明示的に設定されているか。開店前チェックで使う。 */
export function hasExplicitTaxRate(shop: TaxShopLike | null | undefined): boolean {
  const raw = shop?.taxRate;
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 && raw < 1;
}

/** 内税表示の店か。 */
export function isTaxIncluded(shop: TaxShopLike | null | undefined): boolean {
  return shop?.priceDisplayType === 'tax_included';
}

/**
 * 客に上乗せする税額。
 * 内税の店は 0（値段に含まれているので別途足さない）。
 */
export function taxOn(base: number, shop: TaxShopLike | null | undefined): number {
  if (!Number.isFinite(base) || base <= 0) return 0;
  if (isTaxIncluded(shop)) return 0;
  // 端数は切り捨て。アプリ側は floorToDouble、PC は Math.round だったため
  // 同じ伝票が1円ずれていた。切り捨てに揃えている（tax_rate.dart も同じ）。
  return Math.floor(base * resolveTaxRate(shop));
}

/**
 * バック・売上按分の基準額。
 * 内税の店は割り戻し、外税の店はそのまま（税を足す前の額が基準）。
 */
export function taxBase(gross: number, shop: TaxShopLike | null | undefined): number {
  if (!Number.isFinite(gross) || gross === 0) return 0;
  if (!isTaxIncluded(shop)) return gross;
  return gross / (1 + resolveTaxRate(shop));
}

/** 内税の税込額から税額を逆算する（レシート印字用）。 */
export function taxFromGross(gross: number, shop: TaxShopLike | null | undefined): number {
  if (!Number.isFinite(gross) || gross <= 0) return 0;
  const rate = resolveTaxRate(shop);
  if (rate === 0) return 0;
  return Math.floor(gross - gross / (1 + rate));
}

/** 「消費税 (10%)」の表示に使う。 */
export function taxRatePercentLabel(shop: TaxShopLike | null | undefined): string {
  return `${(resolveTaxRate(shop) * 100).toFixed(resolveTaxRate(shop) * 100 % 1 === 0 ? 0 : 1)}%`;
}
